/**
 * src/lib/data/audit.ts — audit log read wrapper (Phase 5.7b).
 *
 * Pairs with the worker's GET /api/audit endpoint. The log is written
 * server-side on every mutation (see worker.js recordWrite); this
 * module is the read path used by the /admin/audit view.
 *
 * Per §1.4, the log is open-read with the view password — no edit
 * password or admin gating client-side. Anyone curious about who
 * changed what can browse.
 */
import type { AuditEntry } from "./types";

export interface AuditFetchOptions {
  workerUrl: string;
  viewPassword: string;
  /** Max entries to return. Worker caps at 500. Default 50. */
  limit?: number;
  /** ISO timestamp; only entries with ts >= since are returned. */
  since?: string;
  signal?: AbortSignal;
}

export class AuditApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "AuditApiError";
    this.status = status;
    this.body = body;
  }
}

export async function listAuditEntries(opts: AuditFetchOptions): Promise<AuditEntry[]> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set("limit", String(opts.limit));
  if (opts.since) qs.set("since", opts.since);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const resp = await fetch(`${opts.workerUrl}/api/audit${suffix}`, {
    headers: { "X-View-Password": opts.viewPassword },
    signal: opts.signal,
  });
  let body: any = null;
  try { body = await resp.json(); } catch {}
  if (!resp.ok) {
    throw new AuditApiError(
      `listAuditEntries failed: ${resp.status} ${body?.error || resp.statusText}`,
      resp.status,
      body,
    );
  }
  return Array.isArray(body) ? body : [];
}
